import { useState, useEffect } from 'react';
import { SipConfig } from '../../hooks/useWebRTC';
import { useWebRTCContext } from '../../context/WebRTCContext';

const EXTENSIONS: Record<string, string> = {
  '771': 'Александр',
  '772': 'Сергей',
  '773': 'Диас',
  '774': 'Вадим',
};

const STATE_LABELS: Record<string, string> = {
  idle: 'Не подключён',
  registering: 'Подключение...',
  registered: 'Готов',
  calling: 'Набор...',
  ringing: 'Входящий звонок',
  active: 'Разговор',
  error: 'Ошибка',
};

const STATE_COLORS: Record<string, string> = {
  idle: '#6b7280',
  registering: '#f59e0b',
  registered: '#10b981',
  calling: '#818cf8',
  ringing: '#f59e0b',
  active: '#10b981',
  error: '#ef4444',
};

interface Props {
  onClose: () => void;
  externalCall?: string; // номер для автодозвона
  onExternalCallHandled?: () => void;
}

export default function WebRTCPhone({ onClose, externalCall, onExternalCallHandled }: Props) {
  const { callState, sipConfig, incomingCall, activeNumber, error, register, unregister, call, answer, hangup } = useWebRTCContext();

  const [showSetup, setShowSetup] = useState(!sipConfig);
  const [ext, setExt] = useState(sipConfig?.extension ?? '771');
  const [pass, setPass] = useState(sipConfig?.password ?? '');
  const [dialInput, setDialInput] = useState('');

  // Auto-call when external number passed
  useEffect(() => {
    if (externalCall && callState === 'registered') {
      call(externalCall);
      onExternalCallHandled?.();
    }
  }, [externalCall, callState]); // eslint-disable-line

  const handleRegister = () => {
    const config: SipConfig = {
      extension: ext,
      password: pass,
      wsUrl: `wss://${(window as any).keyoffDesktop ? new URL((window as any).keyoffDesktop.serverOrigin).host : window.location.host}/ws-pbx`,
      sipDomain: '85.202.193.35',
    };
    register(config);
    setShowSetup(false);
  };

  const handleDial = () => {
    if (dialInput.trim()) {
      call(dialInput.trim());
      setDialInput('');
    }
  };

  const isActive = callState === 'active' || callState === 'calling' || callState === 'ringing';

  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
      width: 280, background: '#0f1117', border: '1px solid #1f2937',
      borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ background: '#131926', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #1f2937' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: STATE_COLORS[callState] ?? '#6b7280',
            boxShadow: callState === 'registered' || callState === 'active' ? `0 0 6px ${STATE_COLORS[callState]}` : 'none',
          }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#f9fafb' }}>
            {sipConfig ? `${EXTENSIONS[sipConfig.extension] ?? sipConfig.extension} (${sipConfig.extension})` : 'Телефон'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowSetup(!showSetup)} title="Настройки"
            style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14, padding: 2 }}>
            ⚙
          </button>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 16, padding: 2 }}>
            ×
          </button>
        </div>
      </div>

      {/* Setup panel */}
      {showSetup && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #1f2937' }}>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Настройки SIP</div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 3 }}>Добавочный</label>
            <select value={ext} onChange={e => setExt(e.target.value)}
              style={{ width: '100%', background: '#131926', border: '1px solid #374151', borderRadius: 6, padding: '6px 8px', color: '#f9fafb', fontSize: 13 }}>
              {Object.entries(EXTENSIONS).map(([num, name]) => (
                <option key={num} value={num}>{num} — {name}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 3 }}>Пароль</label>
            <input type="password" value={pass} onChange={e => setPass(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRegister()}
              placeholder="SIP пароль"
              style={{ width: '100%', background: '#131926', border: '1px solid #374151', borderRadius: 6, padding: '6px 8px', color: '#f9fafb', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleRegister}
              style={{ flex: 1, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {callState === 'registering' ? 'Подключение...' : 'Подключиться'}
            </button>
            {callState !== 'idle' && callState !== 'error' && (
              <button onClick={unregister}
                style={{ background: '#374151', color: '#9ca3af', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}>
                Выйти
              </button>
            )}
          </div>
        </div>
      )}

      {/* Status */}
      <div style={{ padding: '8px 16px', background: '#0a0d14', borderBottom: '1px solid #1f2937', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, color: STATE_COLORS[callState] ?? '#6b7280', fontWeight: 600 }}>
          {STATE_LABELS[callState] ?? callState}
        </span>
        {activeNumber && <span style={{ fontSize: 12, color: '#9ca3af' }}>— {activeNumber}</span>}
      </div>

      {error && (
        <div style={{ padding: '6px 16px', background: 'rgba(239,68,68,0.1)', borderBottom: '1px solid #374151' }}>
          <span style={{ fontSize: 11, color: '#ef4444' }}>{error}</span>
        </div>
      )}

      {/* Incoming call UI */}
      {callState === 'ringing' && incomingCall && (
        <div style={{ padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#f9fafb', fontWeight: 600, marginBottom: 4 }}>
            📞 {incomingCall.displayName}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>{incomingCall.from}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={answer}
              style={{ flex: 1, background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 20, cursor: 'pointer' }}>
              📞
            </button>
            <button onClick={hangup}
              style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 20, cursor: 'pointer' }}>
              📵
            </button>
          </div>
        </div>
      )}

      {/* Active call UI */}
      {callState === 'active' && (
        <div style={{ padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#f9fafb', marginBottom: 12 }}>Разговор: {activeNumber}</div>
          <button onClick={hangup}
            style={{ width: '100%', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 20, cursor: 'pointer' }}>
            📵 Завершить
          </button>
        </div>
      )}

      {/* Dialer */}
      {callState === 'registered' && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={dialInput}
              onChange={e => setDialInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleDial()}
              placeholder="+7 747 ..."
              style={{ flex: 1, background: '#131926', border: '1px solid #374151', borderRadius: 8, padding: '7px 10px', color: '#f9fafb', fontSize: 13 }}
            />
            <button onClick={handleDial}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 18, cursor: 'pointer' }}>
              📞
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
